import StageType from "@/types/stage-type";
import SongType from "@/types/song-type";
import { sample } from "lodash";
import { mediaCache } from "@/utils/media-cache";
import { extractAssetId } from "@/utils/asset-utils";
import { STAGE_NAMES } from "@/constants/stage-names";

class StageAssetPreloader {
  // Track preloading promises to avoid duplicate downloads
  private preloadingAssets = new Map<string, Promise<string>>();

  // Store selected vocabulary images for each vocabulary ID
  private selectedVocabularyImages = new Map<number, string>();

  // Map videoId to URL to reuse same URL for same video (avoid re-downloading due to signature changes)
  private videoIdToUrl = new Map<string, string>();

  // Extract videoId from URL (filename without signature)
  // Uses shared utility function
  private extractVideoId(url: string): string | undefined {
    return extractAssetId(url);
  }

  // Get or store video URL by videoId to ensure same URL is reused for same video
  private getCanonicalVideoUrl(url: string): string {
    const videoId = this.extractVideoId(url);
    if (!videoId) return url;

    // If we already have a URL for this videoId, reuse it
    const existingUrl = this.videoIdToUrl.get(videoId);
    if (existingUrl) {
      return existingUrl;
    }

    // Otherwise, store this URL as the canonical one for this videoId
    this.videoIdToUrl.set(videoId, url);
    return url;
  }

  async preloadStage(
    stage: StageType | undefined,
    song: SongType,
  ): Promise<void> {
    if (!stage) return;

    // Build ordered asset list: for each vocabulary cue, load image first, then related videos
    const assetsToPreload: Array<{
      url: string;
      type: "video" | "audio" | "image";
      priority: number;
    }> = [];

    let priority = 0;

    // First, collect prompt video (if exists) - highest priority
    if (stage.promptVideo?.url) {
      const videoId = this.extractVideoId(stage.promptVideo.url);
      if (videoId) {
        // Use canonical URL (reuse existing URL if same video)
        const canonicalUrl = this.getCanonicalVideoUrl(stage.promptVideo.url);
        if (!this.preloadingAssets.has(canonicalUrl)) {
          assetsToPreload.push({
            url: canonicalUrl,
            type: "video",
            priority: priority++,
          });
        }
      }
    }

    // Preload instrumental audio for "Sing and Instrument" stage
    // High priority to ensure audio is ready before playback starts
    if (
      song.instumentalAudio?.url &&
      stage.name === STAGE_NAMES.SING_AND_INSTRUMENT
    ) {
      const audioUrl = song.instumentalAudio.url;
      if (!this.preloadingAssets.has(audioUrl)) {
        assetsToPreload.push({
          url: audioUrl,
          type: "audio",
          priority: priority++,
        });
      }
    }

    // For each vocabulary cue, load image first, then its video
    if (stage.vocabularyCues && song.vocabularies) {
      stage.vocabularyCues.forEach((cue) => {
        const vocabulary = song.vocabularies.find(
          (v) => v.id === cue.vocabularyId,
        );

        if (vocabulary?.images && vocabulary.images.length > 0) {
          // Select one random image for this vocabulary (if not already selected)
          let selectedImageUrl = this.selectedVocabularyImages.get(
            cue.vocabularyId,
          );

          if (!selectedImageUrl) {
            const selectedImage = sample(vocabulary.images);
            selectedImageUrl = selectedImage?.url;
            if (selectedImageUrl) {
              this.selectedVocabularyImages.set(
                cue.vocabularyId,
                selectedImageUrl,
              );
            }
          }

          // Preload the selected image
          if (
            selectedImageUrl &&
            !this.preloadingAssets.has(selectedImageUrl)
          ) {
            assetsToPreload.push({
              url: selectedImageUrl,
              type: "image",
              priority: priority++,
            });
          }
        }

        // Then preload vocabulary cue video (if exists)
        if (cue.video && cue.video.trim() !== "") {
          const videoId = this.extractVideoId(cue.video);
          if (videoId) {
            // Use canonical URL (reuse existing URL if same video)
            const canonicalUrl = this.getCanonicalVideoUrl(cue.video);
            if (!this.preloadingAssets.has(canonicalUrl)) {
              assetsToPreload.push({
                url: canonicalUrl,
                type: "video",
                priority: priority++,
              });
            }
          }
        }
      });
    }

    // Then the main stage video
    if (stage.video?.url) {
      const videoId = this.extractVideoId(stage.video.url);
      if (videoId) {
        // Use canonical URL (reuse existing URL if same video)
        const canonicalUrl = this.getCanonicalVideoUrl(stage.video.url);
        if (!this.preloadingAssets.has(canonicalUrl)) {
          assetsToPreload.push({
            url: canonicalUrl,
            type: "video",
            priority: priority++,
          });
        }
      }
    }

    // Solution video
    if (stage.solutionVideo?.url) {
      const videoId = this.extractVideoId(stage.solutionVideo.url);
      if (videoId) {
        // Use canonical URL (reuse existing URL if same video)
        const canonicalUrl = this.getCanonicalVideoUrl(stage.solutionVideo.url);
        if (!this.preloadingAssets.has(canonicalUrl)) {
          assetsToPreload.push({
            url: canonicalUrl,
            type: "video",
            priority: priority++,
          });
        }
      }
    }

    // Hint audios (lowest priority)
    if (stage.hintAudios) {
      stage.hintAudios.forEach((audio) => {
        if (audio.url && !this.preloadingAssets.has(audio.url)) {
          assetsToPreload.push({
            url: audio.url,
            type: "audio",
            priority: priority++,
          });
        }
      });
    }

    // Sort by priority and preload using fetch + cache (ensures complete download)
    assetsToPreload.sort((a, b) => a.priority - b.priority);

    // Preload assets sequentially to respect priority order
    // Use Promise.allSettled to continue even if some fail
    const preloadPromises = assetsToPreload.map(async ({ url, type }) => {
      // Check if already preloading
      if (this.preloadingAssets.has(url)) {
        return await this.preloadingAssets.get(url)!;
      }

      // Start preloading using mediaCache (ensures complete download and caching)
      const preloadPromise = mediaCache
        .downloadAndCache(url, type as "video" | "audio" | "image")
        .then((blobUrl) => {
          console.log(
            `Preloaded: ${type}:${url.split("/").pop()?.split("?")[0]}`,
          );

          // Preloading only warms the cache - nothing renders this URL, and
          // every caller now gets its own, so releasing it here frees the
          // blob instead of retaining it for the life of the page. The
          // player mints a fresh URL from the cache when it needs one.
          if (blobUrl.startsWith("blob:")) {
            mediaCache.revokeBlobUrl(blobUrl);
          }

          return blobUrl;
        })
        .catch((error) => {
          console.error(`Failed to preload ${type}:`, url, error);
          throw error;
        })
        .finally(() => {
          // Remove from preloading map when done
          this.preloadingAssets.delete(url);
        });

      this.preloadingAssets.set(url, preloadPromise);
      return preloadPromise;
    });

    // Wait for all preloads to complete (or fail)
    await Promise.allSettled(preloadPromises);
  }

  // Get the selected image URL for a vocabulary
  getSelectedVocabularyImage(vocabularyId: number): string | undefined {
    return this.selectedVocabularyImages.get(vocabularyId);
  }

  // Note: No need for remove() method anymore since we're using mediaCache
  // Assets are managed by mediaCache's LRU system

  clear(): void {
    // Clear preloading promises (but don't cancel ongoing downloads)
    this.preloadingAssets.clear();
    // Note: Do NOT clear videoIdToUrl and selectedVocabularyImages here
    // Reason:
    // - videoIdToUrl: Keep using same URL for same video to leverage browser cache
    // - selectedVocabularyImages: Keep vocabulary image selections consistent across page navigations
    // If user goes from song detail page -> play page -> back -> play again,
    // they should see the same vocabulary images and videos load from cache
    // Note: mediaCache manages its own cache, so we don't need to clear it here
  }

  // Explicitly clear all cached mappings (call when truly needed, e.g., app reset or switching songs)
  clearAllCaches(): void {
    this.videoIdToUrl.clear();
    this.selectedVocabularyImages.clear();
  }
}

export const stageAssetPreloader = new StageAssetPreloader();
